import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
} from "reactstrap";
import { useDebounce } from "use-debounce";
import { toast } from "react-toastify";

import MenuBar from "../../../components/MenuBar";
import { DocumentTable } from "../../../components/DocumentTable";
import Loader from "../../../common/Loader";
import NoDataMessage from "../../../common/NoDataMessage";
import {
  ContentBatchRow,
  ContentDifficulty,
  ContentQuestionRow,
  ContentQuestionState,
} from "../../../common/contentTypes";
import * as Content from "../../../services/api/Content";
import { EvalControl } from "../EvalControl";
import { FlagCaptureModal } from "../FlagCaptureModal";
import { QuestionEditorModal } from "../QuestionEditorModal";
import { categoryLabel, useCategories } from "../useCategories";
import { CategorySelect } from "../CategorySelect";
import { ContentSectionTabs } from "../ContentSectionTabs";
import { SearchableSelect } from "../SearchableSelect";
import { QuestionDetail } from "../QuestionDetail";

const PAGE_SIZE = 25;

interface Filters {
  search: string;
  categoryId: string;
  difficulty: string;
  state: string;
  batchId: string;
}

const EMPTY_FILTERS: Filters = {
  search: "",
  categoryId: "",
  difficulty: "",
  state: "",
  batchId: "",
};

/**
 * The states an editor actually asks for, rather than one checkbox per state.
 * The default omits `deleted` because a takedown should stay out of the way
 * until you go looking for it; `undefined` states lets the server apply its own
 * default (active + draft).
 */
const STATE_FILTERS: Record<
  string,
  { label: string; states?: ContentQuestionState[] }
> = {
  "": { label: "Active & draft", states: undefined },
  active: { label: "Active only", states: ["active"] },
  draft: { label: "Draft only", states: ["draft"] },
  deleted: { label: "Deleted only", states: ["deleted"] },
  all: { label: "All states", states: ["active", "draft", "deleted"] },
};

const difficultyColor = (difficulty: string) => {
  if (difficulty === "easy") return "success";
  if (difficulty === "hard") return "danger";
  return "warning";
};

const stateColor = (state: ContentQuestionState) => {
  if (state === "active") return "success";
  if (state === "deleted") return "danger";
  return "secondary";
};

/** DocumentTable keys rows off `_id`; content ids live on `id`. */
type QuestionTableRow = ContentQuestionRow & { _id: string };

export const ContentQuestionsPage: React.FC = () => {
  const { categories } = useCategories();
  const [batches, setBatches] = useState<ContentBatchRow[]>([]);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [debouncedFilters] = useDebounce(filters, 500);
  const [page, setPage] = useState(0);
  const [questions, setQuestions] = useState<ContentQuestionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ContentQuestionRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [flagging, setFlagging] = useState<ContentQuestionRow[] | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await Content.listQuestions({
        search: debouncedFilters.search.trim() || undefined,
        categoryIds: debouncedFilters.categoryId
          ? [debouncedFilters.categoryId]
          : undefined,
        difficulty:
          (debouncedFilters.difficulty as ContentDifficulty) || undefined,
        states: STATE_FILTERS[debouncedFilters.state]?.states,
        batchId: debouncedFilters.batchId || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setQuestions(result.items ?? []);
      setTotal(result.total ?? 0);
    } catch (err) {
      toast.error(`Failed to load questions: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    Content.listBatches()
      .then((result) => setBatches(result.items ?? []))
      .catch(() => setBatches([]));
  }, []);

  const setFilter = (patch: Partial<Filters>) => {
    setPage(0);
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const hasMore = (page + 1) * PAGE_SIZE < total;
  const selectedRows = questions.filter((question) => selected[question.id]);

  const rows: QuestionTableRow[] = questions.map((question) => ({
    ...question,
    _id: question.id,
  }));

  const batchName = (batchId: string) =>
    batches.find((batch) => batch.id === batchId)?.name ?? batchId;

  const properties = [
    {
      title: "",
      style: { width: "2rem" },
      render: (question: QuestionTableRow) => (
        <Input
          type="checkbox"
          className="position-static m-0"
          checked={!!selected[question.id]}
          onChange={(e) =>
            setSelected((prev) => ({
              ...prev,
              [question.id]: e.target.checked,
            }))
          }
        />
      ),
    },
    {
      title: "Title",
      render: (question: QuestionTableRow) => (
        <span
          title={question.title}
          className="d-inline-block text-truncate"
          style={{ maxWidth: "22rem" }}
        >
          {question.title}
        </span>
      ),
    },
    {
      title: "Category",
      render: (question: QuestionTableRow) =>
        categoryLabel(categories, question.categoryId),
    },
    {
      title: "Difficulty",
      render: (question: QuestionTableRow) => (
        <Badge color={difficultyColor(question.difficulty)}>
          {question.difficulty}
        </Badge>
      ),
    },
    {
      // The correct answer is the only choice worth surfacing in a dense table;
      // the rest are a count. Expanding the row shows all four.
      title: "Choices",
      render: (question: QuestionTableRow) => {
        const correct = question.choices.find((choice) => choice.correct);
        const others = question.choices.length - (correct ? 1 : 0);
        return (
          <span title={question.choices.map((c) => c.label).join(" · ")}>
            {correct ? (
              <>
                <i className="fas fa-check text-success mr-1" />
                {correct.label}
              </>
            ) : (
              <span className="text-muted">—</span>
            )}
            {others > 0 && <span className="text-muted"> · +{others}</span>}
          </span>
        );
      },
    },
    {
      title: "State",
      render: (question: QuestionTableRow) => (
        <>
          <Badge color={stateColor(question.state)}>{question.state}</Badge>
          {question.reviewStatus && question.reviewStatus !== "approved" && (
            <Badge color="info" className="ml-1">
              {question.reviewStatus}
            </Badge>
          )}
        </>
      ),
    },
    {
      title: "Batch",
      render: (question: QuestionTableRow) =>
        question.batchId ? (
          batchName(question.batchId)
        ) : (
          <span className="text-muted" title="Imported bank content">
            —
          </span>
        ),
    },
    {
      title: "Updated",
      render: (question: QuestionTableRow) => (
        <span
          title={question.updatedBy ? `by ${question.updatedBy}` : undefined}
        >
          {question.updatedAt ? question.updatedAt.slice(0, 10) : "—"}
        </span>
      ),
    },
    {
      title: "Feedback",
      render: (question: QuestionTableRow) => (
        <EvalControl questionId={question.id} />
      ),
    },
    {
      title: "",
      render: (question: QuestionTableRow) => (
        <>
          <Button
            size="sm"
            color="link"
            className="p-1"
            title="Edit"
            onClick={() => setEditing(question)}
          >
            <i className="fas fa-pencil-alt" />
          </Button>
          <Button
            size="sm"
            color="link"
            className="p-1 text-danger"
            title="Flag for review"
            onClick={() => setFlagging([question])}
          >
            <i className="fas fa-flag" />
          </Button>
        </>
      ),
    },
  ];

  return (
    <div>
      <MenuBar />
      <div className="container-fluid mt-3 pb-5">
        <ContentSectionTabs active="questions" />
        <Card className="mb-3 bg-light">
          <CardBody>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <h1 className="mb-0">Questions</h1>
              <Button color="primary" onClick={() => setCreating(true)}>
                <i className="fas fa-plus mr-1" /> Add Question
              </Button>
            </div>
            <Row form className="mb-2">
              <Col md={12}>
                <InputGroup>
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="fas fa-search" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    placeholder="Search question text…"
                    value={filters.search}
                    onChange={(e) => setFilter({ search: e.target.value })}
                  />
                </InputGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={5}>
                <CategorySelect
                  categories={categories}
                  value={filters.categoryId}
                  onChange={(categoryId) => setFilter({ categoryId })}
                  emptyOption="All categories"
                />
              </Col>
              <Col md={3}>
                <Input
                  type="select"
                  className="custom-select"
                  value={filters.difficulty}
                  onChange={(e) => setFilter({ difficulty: e.target.value })}
                >
                  <option value="">All difficulties</option>
                  <option value="easy">easy</option>
                  <option value="medium">medium</option>
                  <option value="hard">hard</option>
                </Input>
              </Col>
              <Col md={4}>
                <Input
                  type="select"
                  className="custom-select"
                  value={filters.state}
                  onChange={(e) => setFilter({ state: e.target.value })}
                >
                  {Object.entries(STATE_FILTERS).map(([value, { label }]) => (
                    <option key={value || "default"} value={value}>
                      {label}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={5} className="mt-2">
                <SearchableSelect
                  value={filters.batchId}
                  onChange={(batchId) => setFilter({ batchId })}
                  emptyOption="All batches"
                  options={batches.map((batch) => ({
                    value: batch.id,
                    label: batch.name,
                  }))}
                />
              </Col>
            </Row>
          </CardBody>
        </Card>

        {selectedRows.length > 0 && (
          <div className="mb-2">
            <Button
              color="danger"
              size="sm"
              onClick={() => setFlagging(selectedRows)}
            >
              Flag & remove {selectedRows.length}
            </Button>
            <Button
              color="link"
              size="sm"
              onClick={() => setSelected({})}
              className="text-muted"
            >
              Clear selection
            </Button>
          </div>
        )}

        {loading ? (
          <Loader light />
        ) : rows.length === 0 ? (
          <NoDataMessage light componentName="question" />
        ) : (
          <>
            {total > rows.length && (
              <div className="bg-light border rounded px-3 py-2 mb-2 text-muted">
                {page === 0
                  ? `Showing first ${rows.length}`
                  : `Showing ${page * PAGE_SIZE + 1}–${
                      page * PAGE_SIZE + rows.length
                    }`}{" "}
                of {total} matching questions. Refine your search or filters to
                narrow results.
              </div>
            )}
            <DocumentTable<QuestionTableRow>
              light
              objects={rows}
              renderExpanded={(question) => (
                <QuestionDetail
                  question={question}
                  categoryLabel={categoryLabel(categories, question.categoryId)}
                />
              )}
              properties={properties}
            />
            {/* Grouped — edge-to-edge puts the buttons a screen apart. */}
            <div className="d-flex justify-content-center align-items-center my-3">
              <Button
                size="sm"
                color="secondary"
                outline
                disabled={page === 0}
                onClick={() => setPage((prev) => Math.max(0, prev - 1))}
              >
                <i className="fas fa-chevron-left mr-1" /> Previous
              </Button>
              <small className="text-muted mx-3">
                {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + rows.length} of{" "}
                {total}
              </small>
              <Button
                size="sm"
                color="secondary"
                outline
                disabled={!hasMore}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Next <i className="fas fa-chevron-right ml-1" />
              </Button>
            </div>
          </>
        )}
      </div>

      {(editing || creating) && (
        <QuestionEditorModal
          question={editing ?? undefined}
          categories={categories}
          isOpen
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            load();
          }}
          onProposeCategory={() => {
            // The Categories screen owns creation; keep the edit open behind it
            // so nothing typed here is lost.
            window.open("/content/categories", "_blank");
          }}
          onFlagAndRemove={(question) => {
            setEditing(null);
            setCreating(false);
            setFlagging([question]);
          }}
        />
      )}

      {flagging && (
        <FlagCaptureModal
          questions={flagging}
          isOpen
          onClose={() => setFlagging(null)}
          onFlagged={() => {
            setFlagging(null);
            setSelected({});
            load();
          }}
        />
      )}
    </div>
  );
};
